import { DataType } from "@/components/DataTable/Models";
import { Software } from "@/services/global-services/models";
import { IconTable } from "@codexteam/icons";
import { API, BlockAPI, BlockTool } from "@editorjs/editorjs";
import { BlockToolConstructorOptions, BlockToolData } from "@editorjs/editorjs/types/tools";
import { createRoot } from "react-dom/client";
import { DocumentationTableColumnData } from "../DocumentationTableBlock/DocumentationTableBlockTool";
import { InformationTableBlockComponent } from "./InformationTableBlockComponent";

export interface InformationTableBlockData extends BlockToolData {
    title?: string;
    caption?: string;
    columns: DocumentationTableColumnData[];
    hasSoftwareColumns: boolean;
}

export interface InformationTableBlockConfig {
    currentLanguage: string;
    software: Software[];
}

const DEFAULT_CONFIG: { [key: string]: InformationTableBlockData } = {
    "int-en": {
        title: "Information level",
        columns: [
            {
                value: "Property",
                dataType: DataType.STRING,
            },
            {
                value: "GID\r\nx1x",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nx2x",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nx3x",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nx4x",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nx5x",
                dataType: DataType.BOOLEAN,
            },
        ],
        hasSoftwareColumns: true,
    },

    "int-fr": {
        title: "NIVEAU D'INFORMATION",
        columns: [
            {
                value: "Propriété",
                dataType: DataType.STRING,
            },
            {
                value: "GID\r\nx1x",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nx2x",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nx3x",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nx4x",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nx5x",
                dataType: DataType.BOOLEAN,
            },
        ],
        hasSoftwareColumns: true,
    },

    "int-nl": {
        title: "Informatieniveau",
        columns: [
            {
                value: "Eigenschappen",
                dataType: DataType.STRING,
            },
            {
                value: "GID\r\nx1x",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nx2x",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nx3x",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nx4x",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nx5x",
                dataType: DataType.BOOLEAN,
            },
        ],
        hasSoftwareColumns: true,
    },

    "int-de": {
        title: "Information level",
        columns: [
            {
                value: "Property",
                dataType: DataType.STRING,
            },
            {
                value: "GID\r\nx1x",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nx2x",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nx3x",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nx4x",
                dataType: DataType.BOOLEAN,
            },
            {
                value: "GID\r\nx5x",
                dataType: DataType.BOOLEAN,
            },
        ],
        hasSoftwareColumns: true,
    },
};

export class InformationTableBlockTool implements BlockTool {
    public static get toolbox() {
        return {
            title: "GID - Information",
            icon: IconTable,
        };
    }

    api: API;
    block?: BlockAPI;
    config: InformationTableBlockData;
    data: InformationTableBlockData;
    currentData: InformationTableBlockData;
    software: Software[];
    CSS: { [key: string]: string };
    nodes: { [key: string]: HTMLElement | null };

    // Use `any` to circumvent an issue with Editor.js when using TypeScript
    constructor(params: BlockToolConstructorOptions<InformationTableBlockData, InformationTableBlockConfig>) {
        const { api, block, config, data } = params;
        if (!config) throw new Error("InformationTableBlockTool configuration missing");

        this.api = api;
        this.block = block;
        this.data = data;
        this.config = DEFAULT_CONFIG[config.currentLanguage]!;
        this.software = config.software;

        if (data && Object.keys(data).length > 0) {
            this.data = data;
        } else {
            this.data = JSON.parse(JSON.stringify(DEFAULT_CONFIG[config.currentLanguage]!));
        }

        this.currentData = JSON.parse(JSON.stringify(this.data));

        this.CSS = {
            wrapper: "template-editor-content-block",
        };

        this.nodes = {
            holder: null,
            table: null,
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    save(block: HTMLElement) {
        return {
            title: this.api.sanitizer.clean(this.currentData.title || "", {}),
            columns: this.currentData.columns.map((column) => ({
                ...column,
                value: this.api.sanitizer.clean(column.value || "", {}),
            })),
            caption: this.api.sanitizer.clean(this.currentData.caption || "", {}),
            hasSoftwareColumns: true,
            showGroupHeaderRows: true,
            hasPropertyLevel: true,
        };
    }

    render(): HTMLElement {
        if (!this.nodes.table) {
            const rootNode = document.createElement("div");
            rootNode.setAttribute("class", this.CSS.wrapper);
            this.nodes.table = rootNode;

            const root = createRoot(rootNode);
            root.render(
                <InformationTableBlockComponent
                    data={this.data}
                    defaults={this.config}
                    software={this.software}
                    onChange={(data) => {
                        this.currentData = data;
                        this.block?.dispatchChange();
                    }}
                />
            );
        }

        if (!this.nodes.holder) {
            const holderNode = document.createElement("div");
            holderNode.setAttribute("class", this.CSS.wrapper);
            holderNode.appendChild(this.nodes.table);
            this.nodes.holder = holderNode;
        }

        return this.nodes.holder;
    }
}
